import { useEffect, useState } from "react";
import axios from "axios";
import {
  Users, Plus, Pencil, Trash2, X, Eye, EyeOff, Search, Shield,
  UserCheck, RefreshCw, KeyRound, Mail, User,
} from "lucide-react";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";
import "./UsersPage.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

const ALL_ROLES = [
  { value: "admin",                label: "Admin",                color: "#f87171", bg: "rgba(239,68,68,0.1)" },
  { value: "manager",              label: "Manager",              color: "#818cf8", bg: "rgba(99,102,241,0.12)" },
  { value: "frontdesk",            label: "Front Desk",           color: "#34d399", bg: "rgba(52,211,153,0.1)" },
  { value: "reception",            label: "Reception",            color: "#34d399", bg: "rgba(52,211,153,0.1)" },
  { value: "room_service",         label: "Room Service",         color: "#fbbf24", bg: "rgba(251,191,36,0.1)" },
  { value: "room_service_manager", label: "RS Manager",           color: "#fb923c", bg: "rgba(251,146,60,0.1)" },
  { value: "restaurant",           label: "Restaurant",           color: "#a78bfa", bg: "rgba(167,139,250,0.1)" },
  { value: "spa",                  label: "Spa",                  color: "#06b6d4", bg: "rgba(6,182,212,0.1)" },
  { value: "livechat",             label: "Live Chat",            color: "#a78bfa", bg: "rgba(167,139,250,0.1)" },
];

function roleMeta(role: string) {
  return ALL_ROLES.find(r => r.value === role) || { label: role, color: "rgba(242,235,217,0.4)", bg: "rgba(100,116,139,0.1)" };
}

function initials(name: string) {
  return name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
}

interface StaffUser {
  id: string;
  name: string;
  email: string;
  role: string;
  hotel_id: string;
  created_at: string;
  last_login?: string;
}

const BLANK_NEW = { name: "", email: "", password: "", role: "reception" };

export default function UsersPage() {
  const [users, setUsers]       = useState<StaffUser[]>([]);
  const [loading, setLoading]   = useState(false);
  const [search, setSearch]     = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser]       = useState({ ...BLANK_NEW });
  const [showNewPw, setShowNewPw]   = useState(false);
  const [creating, setCreating]     = useState(false);
  const [createErr, setCreateErr]   = useState("");

  // Edit modal
  const [editing, setEditing]     = useState<StaffUser | null>(null);
  const [editName, setEditName]   = useState("");
  const [editRole, setEditRole]   = useState("");
  const [editPw, setEditPw]       = useState("");
  const [showEditPw, setShowEditPw] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [editErr, setEditErr]     = useState("");

  const token = localStorage.getItem("ai2_token");
  const myId  = (() => {
    try { return JSON.parse(atob(token!.split(".")[1])).id; } catch { return null; }
  })();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/auth/users`);
      setUsers(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = users.filter(u => {
    const matchRole = roleFilter === "all" || u.role === roleFilter;
    const matchQ    = !search || u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase());
    return matchRole && matchQ;
  });

  const handleCreate = async () => {
    setCreateErr("");
    if (!newUser.name.trim())     return setCreateErr("Name is required");
    if (!newUser.email.trim())    return setCreateErr("Email is required");
    if (newUser.password.length < 6) return setCreateErr("Password must be at least 6 characters");
    setCreating(true);
    try {
      const hotelId = (() => { try { return JSON.parse(atob(token!.split(".")[1])).hotelId; } catch { return null; } })();
      await axios.post(`${API}/api/auth/create-user`, { ...newUser, hotelId });
      setShowCreate(false);
      setNewUser({ ...BLANK_NEW });
      load();
    } catch (err: any) {
      setCreateErr(err.response?.data?.message || "Failed to create user");
    } finally { setCreating(false); }
  };

  const openEdit = (u: StaffUser) => {
    setEditing(u);
    setEditName(u.name);
    setEditRole(u.role);
    setEditPw("");
    setEditErr("");
  };

  const handleSave = async () => {
    setEditErr("");
    if (!editName.trim()) return setEditErr("Name is required");
    if (editPw && editPw.length < 6) return setEditErr("Password must be at least 6 characters");
    setSaving(true);
    try {
      const body: any = { name: editName, role: editRole };
      if (editPw) body.password = editPw;
      await axios.put(`${API}/api/auth/users/${editing!.id}`, body);
      setEditing(null);
      load();
    } catch (err: any) {
      setEditErr(err.response?.data?.message || "Failed to save");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this user? They will no longer be able to log in.")) return;
    try {
      await axios.delete(`${API}/api/auth/users/${id}`);
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to delete user");
    }
  };

  return (
    <DashboardLayout>
      <div className="up-page">

        {/* Header */}
        <div className="up-header">
          <div>
            <h1 className="up-title">Staff Accounts</h1>
            <p className="up-subtitle">{users.length} user{users.length !== 1 ? "s" : ""} · Manage who can log in to the dashboard</p>
          </div>
          <div className="up-header-right">
            <button className="up-btn-ghost" onClick={load}>
              <RefreshCw size={14} />
            </button>
            <button className="up-btn-primary" onClick={() => { setShowCreate(true); setCreateErr(""); setNewUser({ ...BLANK_NEW }); }}>
              <Plus size={14} /> Add User
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="up-toolbar">
          <div className="up-search">
            <Search size={14} />
            <input placeholder="Search name or email…" value={search} onChange={e => setSearch(e.target.value)} />
            {search && <button onClick={() => setSearch("")}><X size={13} /></button>}
          </div>
          <div className="up-role-filters">
            <button className={`up-role-filter ${roleFilter === "all" ? "active" : ""}`} onClick={() => setRoleFilter("all")}>All</button>
            {ALL_ROLES.map(r => (
              <button
                key={r.value}
                className={`up-role-filter ${roleFilter === r.value ? "active" : ""}`}
                style={roleFilter === r.value ? { color: r.color, background: r.bg, borderColor: r.color + "40" } : {}}
                onClick={() => setRoleFilter(r.value)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* Users grid */}
        {loading
          ? <div className="up-loading"><span className="up-spinner" /> Loading users…</div>
          : filtered.length === 0
          ? <div className="up-empty"><Users size={36} /><p>No users found</p></div>
          : (
            <div className="up-grid">
              {filtered.map(u => {
                const rm = roleMeta(u.role);
                const isMe = u.id === myId;
                return (
                  <div key={u.id} className={`up-card ${isMe ? "me" : ""}`}>
                    <div className="up-card-top">
                      <div className="up-avatar" style={{ background: rm.bg, color: rm.color }}>
                        {initials(u.name)}
                      </div>
                      {isMe && <span className="up-me-tag">You</span>}
                      {!isMe && (
                        <div className="up-card-actions">
                          <button className="up-icon-btn" onClick={() => openEdit(u)} title="Edit"><Pencil size={13} /></button>
                          <button className="up-icon-btn danger" onClick={() => handleDelete(u.id)} title="Delete"><Trash2 size={13} /></button>
                        </div>
                      )}
                    </div>
                    <div className="up-card-name">{u.name}</div>
                    <div className="up-card-email"><Mail size={11} /> {u.email}</div>
                    <div className="up-role-badge" style={{ color: rm.color, background: rm.bg }}>
                      <Shield size={10} /> {rm.label}
                    </div>
                    <div className="up-card-meta">
                      {u.last_login
                        ? <>Last login: {new Date(u.last_login).toLocaleDateString()}</>
                        : "Never logged in"}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        }

        {/* ── Create User Modal ── */}
        {showCreate && (
          <div className="up-overlay" onClick={() => setShowCreate(false)}>
            <div className="up-modal" onClick={e => e.stopPropagation()}>
              <div className="up-modal-header">
                <h2 className="up-modal-title"><UserCheck size={16} /> New Staff Account</h2>
                <button className="up-modal-close" onClick={() => setShowCreate(false)}><X size={16} /></button>
              </div>

              <div className="up-form">
                <div className="up-field">
                  <label><User size={11} /> Full Name</label>
                  <input placeholder="e.g. Sarah Johnson" value={newUser.name} onChange={e => setNewUser(p => ({ ...p, name: e.target.value }))} />
                </div>
                <div className="up-field">
                  <label><Mail size={11} /> Email</label>
                  <input type="email" placeholder="sarah@hotel.com" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} />
                </div>
                <div className="up-field">
                  <label><Shield size={11} /> Role</label>
                  <select value={newUser.role} onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))}>
                    {ALL_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div className="up-field">
                  <label><KeyRound size={11} /> Password</label>
                  <div className="up-pw-wrap">
                    <input type={showNewPw ? "text" : "password"} placeholder="Min 6 characters" value={newUser.password} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))} />
                    <button type="button" onClick={() => setShowNewPw(p => !p)}>
                      {showNewPw ? <EyeOff size={13} /> : <Eye size={13} />}
                    </button>
                  </div>
                </div>
                {createErr && <div className="up-error">{createErr}</div>}
              </div>

              <div className="up-modal-footer">
                <button className="up-btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
                <button className="up-btn-primary" onClick={handleCreate} disabled={creating}>
                  {creating ? <span className="up-spinner" /> : <><Plus size={13} /> Create User</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Edit User Modal ── */}
        {editing && (
          <div className="up-overlay" onClick={() => setEditing(null)}>
            <div className="up-modal" onClick={e => e.stopPropagation()}>
              <div className="up-modal-header">
                <h2 className="up-modal-title"><Pencil size={15} /> Edit {editing.name}</h2>
                <button className="up-modal-close" onClick={() => setEditing(null)}><X size={16} /></button>
              </div>

              <div className="up-form">
                <div className="up-field">
                  <label><User size={11} /> Full Name</label>
                  <input value={editName} onChange={e => setEditName(e.target.value)} />
                </div>
                <div className="up-field">
                  <label><Shield size={11} /> Role</label>
                  <select value={editRole} onChange={e => setEditRole(e.target.value)}>
                    {ALL_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div className="up-field">
                  <label><KeyRound size={11} /> New Password <span className="up-optional">(leave blank to keep current)</span></label>
                  <div className="up-pw-wrap">
                    <input type={showEditPw ? "text" : "password"} placeholder="New password…" value={editPw} onChange={e => setEditPw(e.target.value)} />
                    <button type="button" onClick={() => setShowEditPw(p => !p)}>
                      {showEditPw ? <EyeOff size={13} /> : <Eye size={13} />}
                    </button>
                  </div>
                </div>
                {editErr && <div className="up-error">{editErr}</div>}
              </div>

              <div className="up-modal-footer">
                <button className="up-btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
                <button className="up-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? <span className="up-spinner" /> : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}
